export const MAX_JSON_BYTES = 65536;
export const MAX_JSON_DEPTH = 16;

function scalarUnicode(value: string): boolean {
  for (let i = 0; i < value.length; i++) {
    const c = value.charCodeAt(i);
    if (c >= 0xd800 && c <= 0xdbff) {
      const low = value.charCodeAt(++i);
      if (!(low >= 0xdc00 && low <= 0xdfff)) return false;
    } else if (c >= 0xdc00 && c <= 0xdfff) return false;
  }
  return true;
}

// Parse before schema validation: JSON.parse alone silently drops duplicate keys.
// This bounded reader uses JSON.parse only for individually delimited strings.
export function decodeJSON(input: string | Uint8Array, maxBytes = MAX_JSON_BYTES): unknown {
  const fail = (): never => { throw new Error('Invalid service JSON'); };
  if (input.length > maxBytes) fail();
  const text = typeof input === 'string' ? input : new TextDecoder('utf-8', { fatal: true, ignoreBOM: true }).decode(input);
  if (text.length > maxBytes || !scalarUnicode(text) || new TextEncoder().encode(text).length > maxBytes) fail();
  let offset = 0;
  function space() { while (offset < text.length && /[ \t\r\n]/.test(text[offset]!)) offset++; }
  function string(): string {
    const start = offset++;
    while (offset < text.length) {
      const ch = text[offset++];
      if (ch === '\\') { offset++; continue; }
      if (ch === '"') {
        const value = JSON.parse(text.slice(start, offset)) as string;
        if (!scalarUnicode(value)) fail();
        return value;
      }
    }
    return fail();
  }
  function value(depth: number): unknown {
    space();
    const ch = text[offset];
    if (ch === '"') return string();
    if (ch === '{' || ch === '[') {
      if (depth >= MAX_JSON_DEPTH) fail();
      offset++; space();
      const end = ch === '{' ? '}' : ']';
      const object: Record<string, unknown> = Object.create(null) as Record<string, unknown>;
      const array: unknown[] = [];
      if (text[offset] !== end) for (;;) {
        if (ch === '{') {
          if (text[offset] !== '"') fail();
          const key = string(); space();
          if (text[offset++] !== ':' || Object.hasOwn(object, key)) fail();
          object[key] = value(depth + 1);
        } else array.push(value(depth + 1));
        space();
        if (text[offset] !== ',') break;
        offset++; space();
      }
      if (text[offset++] !== end) fail();
      return ch === '{' ? object : array;
    }
    for (const [literal, result] of [['true', true], ['false', false], ['null', null]] as const) {
      if (text.startsWith(literal, offset)) { offset += literal.length; return result; }
    }
    const number = /^(?:0|[1-9][0-9]*)/.exec(text.slice(offset));
    if (!number) return fail();
    offset += number[0].length;
    const result = Number(number[0]);
    if (!Number.isSafeInteger(result)) fail();
    return result;
  }
  const result = value(0); space();
  if (offset !== text.length) fail();
  return result;
}
